// The increase rule (spec C14 Defaults; Rachy, Oct 28): a renewal's premium is an increase when it is
// up at least the stored percent and at least the stored dollars over this term's premium (totals
// with taxes and fees). One function for the server and the web (S14), so the two never drift:
// before this, the test lived twice (apps/server/src/increase-rule.ts and
// apps/web/src/renewals/renewal-lines.ts) with a comment promising they matched.

export type IncreaseRule = { percent: number; minDollars: number };

/** The rule from a stored value, or null when either number is missing or below zero. */
export function parseIncreaseRule(input: unknown): IncreaseRule | null {
  if (!input || typeof input !== 'object') return null;
  const { percent, minDollars } = input as Record<string, unknown>;
  const ok = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;
  return ok(percent) && ok(minDollars) ? { percent, minDollars } : null;
}

/**
 * The rule the Admin saves in Settings, Defaults, or null: the percent 1 to 100 with at most one
 * decimal, the dollars a whole number, 0 or more.
 */
export function parseIncreaseRuleInput(input: unknown): IncreaseRule | null {
  const rule = parseIncreaseRule(input);
  if (!rule) return null;
  const tenths = rule.percent * 10;
  const oneDecimal = Math.abs(tenths - Math.round(tenths)) < 1e-9;
  if (rule.percent < 1 || rule.percent > 100 || !oneDecimal) return null;
  if (!Number.isSafeInteger(rule.minDollars)) return null;
  return { percent: Math.round(tenths) / 10, minDollars: rule.minDollars };
}

/**
 * Whether a renewal premium is an increase under the rule, at or over (15.0% counts, 14.9% does
 * not). Unknown or zero prior premium: no.
 */
export function isIncrease(
  rule: IncreaseRule,
  prior: number | null,
  renewal: number | null,
): boolean {
  if (prior === null || renewal === null || prior <= 0) return false;
  // In whole cents and tenths of a percent, up / prior >= percent / 100 multiplied out, so exactly
  // 15% (or 12.5%) always counts.
  const priorCents = Math.round(prior * 100);
  const upCents = Math.round(renewal * 100) - priorCents;
  const tenths = Math.round(rule.percent * 10);
  return upCents > 0 && upCents * 1000 >= tenths * priorCents && upCents >= rule.minDollars * 100;
}

/** The rule in words: "15% or more, at any dollar amount" or "15% or more and at least $200". */
export const increaseRuleText = (rule: IncreaseRule) =>
  rule.minDollars > 0
    ? `${rule.percent}% or more and at least ${moneyWhole(rule.minDollars)}`
    : `${rule.percent}% or more, at any dollar amount`;

const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const moneyWhole = (n: number) => dollars.format(n);
